import React, { useState, useContext } from 'react';
import Honeybadger from 'honeybadger-js';
import { PhotoItem } from '../Card';

import { PhotoItemsFragment } from '../../generated/graphql';
import { ItemProps } from './common';
import { resizeImageW } from '../../helpers/mediaApiUrls';
import PhotoCropper from '../Modals/PhotoCropper';
import deletePageItem from '../../helpers/apolloCache/deletePageItem';
import { CropProps } from '../PageItemsRail/ImageField';
import { useUpdatePhotoItemMutation, useDeletePhotoItemMutation } from '../../generated/graphql';
import { TrackingContext } from '../../TrackingContext';
import { imageUrlToImgElement } from '../../helpers/images/processImages';
import { pagesQuery } from '../LeftContainer/graphql';

const Photo: React.FC<ItemProps> = ({ item, page, className, dragProps }) => {
  const [updatePhoto] = useUpdatePhotoItemMutation();
  const [deletePhoto] = useDeletePhotoItemMutation();
  const [saving, setSaving] = useState(false);
  const [cropperFile, setCropperFile] = useState<HTMLImageElement>();
  const { track } = useContext(TrackingContext);
  const itemId = item.id;
  const pageId = page.id;
  const photo = item as PhotoItemsFragment;
  const photoPath = resizeImageW(photo.mediaUrl, 145);

  if (!photoPath) return null;

  const closeCropper = () => setCropperFile(undefined);

  const saveCrop = async ({ crop, rotation }: CropProps) => {
    const variables = {
      attributes: {
        pageId,
        id: itemId,
        height: crop.height || 0,
        width: crop.width || 0,
        cropX: crop.x,
        cropY: crop.y,
        rotation,
      },
    };

    try {
      await updatePhoto({ variables });
    } catch (err) {
      Honeybadger.notify(err, 'Error updating page item photo', { itemId, pageId, Component: 'Photo' });
    }
  };

  const onDelete = async () => {
    setSaving(true);
    try {
      await deletePhoto({
        variables: { attributes: { id: itemId, pageId } },
        update(cache) {
          deletePageItem({ pageId, itemId, cache });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    } catch (err) {
      Honeybadger.notify(err, 'Error deleting page item photo', { itemId, pageId, Component: 'Photo' });
    }

    setSaving(false);
    closeCropper();
  };

  const onClick = async () => {
    track('Item Action Initiated', {
      action: 'edit',
      actionName: 'Edit Page Item',
      page: page.title,
      item: item.type,
    });

    const img = await imageUrlToImgElement(photo.mediaUrl);
    setCropperFile(img);
  };

  const saveImage = async (info: CropProps) => {
    setSaving(true);

    await saveCrop(info);

    closeCropper();
    setSaving(false);
  };

  return (
    <>
      <div data-testid={`photo-item-${itemId}`} onClick={onClick}>
        <PhotoItem photoPath={photoPath} className={className} {...dragProps} />
      </div>
      {cropperFile && (
        <PhotoCropper
          cropperInfo={{
            file: cropperFile,
            onSave: saveImage,
          }}
          onCancel={closeCropper}
          pageId={page.id}
          itemType="PhotoItem"
          loading={saving}
          secondaryActionText="Discard changes"
          primaryActionText="Save Changes"
          currentAspectRatio={photo.aspectRatio}
          defaultLayoutRatioValue={photo.aspectRatio?.value || 1}
          defaultLayoutRatioName={photo.aspectRatio?.name || '1:1'}
          onDeletePhotoHandler={onDelete}
        />
      )}
    </>
  );
};

export default Photo;
